import React from "react";
import {
  ProfileContext,
  defaultProfile,
  Profile
} from "@velaro/velaro-component-lib";
import useApi from "../hooks/useApi";
import authService from "../services/AuthService";

// Seed from the token-exchange profile so the name/avatar shows immediately
// even before Profile/Me returns. The full fetch still runs to get teamIds,
// roles, status settings, etc.
function seedProfile(): Profile {
  const s = authService.profile;
  if (!s || !s.firstName) return defaultProfile;
  return {
    ...defaultProfile,
    picture: s.picture || "",
    firstName: s.firstName || "",
    lastName: s.lastName || "",
    displayName: s.firstName && s.lastName ? `${s.firstName} ${s.lastName}` : (s.userName || ""),
  };
}

export default function ProfileProvider(props: { children: React.ReactNode }) {
  const [loading, setLoading] = React.useState(true);
  const [profile, setProfile] = React.useState<Profile>(seedProfile);
  const api = useApi();

  React.useEffect(() => {
    async function load() {
      try {
        const response = await api.messaging.get("Profile/Me");
        if (response.ok) {
          setProfile(await response.json());
        } else {
          console.error(`[ProfileProvider] Profile/Me returned ${response.status} — JWT mismatch or messaging API error. Name/avatar will use token-exchange data only.`);
        }
      } catch (err) {
        console.error("[ProfileProvider] Profile/Me fetch failed:", err);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [api]);

  const groupMap = React.useMemo(() => {
    const map: { [groupId: number]: boolean } = {};

    if (!profile.teamIds) {
      return map;
    }

    profile.teamIds.forEach((groupId) => {
      map[groupId] = true;
    });

    return map;
  }, [profile.teamIds]);

  const belongsToGroup = React.useCallback(
    (groupId: number) => {
      // null/undefined group id means all users can see.

      if (!groupId) {
        return true;
      }

      return groupMap[groupId] === true;
    },
    [groupMap]
  );

  return (
    <ProfileContext.Provider
      value={{ profile, loading, setProfile, belongsToGroup }}
    >
      {props.children}
    </ProfileContext.Provider>
  );
}
